import { useState, Fragment } from 'react';
import styled from 'styled-components';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  Add as PlusIcon,
  Subtract as MinusIcon
} from '@urbaninfrastructure/react-icons';
import {
  Arrow,
  Flex,
  Box,
  Button,
  IconButton,
  Text,
  I18NBuy
} from '../../../../components';
import { ProductPrice } from '../../../../components/ProductPlanCard';
import { buyGiftCardPageQuery_products } from '../../../../core-types';
import { ConnectionPriceMatrixes } from '../../../../components/ConnectionPriceMatrixes';
import { ShowTariffsMessage } from '../../../../components/ProductPlanCard/ShowTariffsMessage';
import ReactMarkdown from 'react-markdown';

type Props = {
  product: buyGiftCardPageQuery_products;
  quantity?: number;
  onAddClick: () => void;
  onRemoveClick: () => void;
  locked?: boolean;
  active?: boolean;
};

const messages = defineMessages({
  quantity: {
    id: 'modules.BuyGiftCards.GiftCards.quantity',
    defaultMessage: '{quantity} pcs'
  },
  ariaAdd: {
    id: 'modules.BuyGiftCards.GiftCards.ariaAdd',
    defaultMessage: 'Add {name}'
  },
  ariaSubtract: {
    id: 'modules.BuyGiftCards.GiftCards.ariaSubtract',
    defaultMessage: 'Subtract {name}'
  },
  hideTariffs: {
    id: 'modules.BuyGiftCards.GiftCards.hideTariffs',
    defaultMessage: 'Hide tariffs'
  }
});

export const Base = styled(Box)`
  border-bottom: 1px solid ${props => props.theme.colors.borderGray};
  &:last-child {
    border-bottom: none;
  }
`;

const TariffToggler = styled(Button)`
  border-width: 0;
  color: ${({ theme }) => theme.colors.text};
  padding: 0;
  text-decoration: none;
  &:focus,
  &:hover {
    text-decoration: underline;
  }
`;

const Product = ({
  product,
  quantity = 0,
  onAddClick,
  onRemoveClick,
  locked,
  active
}: Props) => {
  const intl = useIntl();
  const [showTariffs, setShowTariffs] = useState(false);
  const { name, descriptionText, connectionPriceMatrixes } = product;
  const tariffs = connectionPriceMatrixes || [];

  return (
    <Base as="li" p={4}>
      <Flex alignItems="center">
        <Box flex={1}>
          <Text typoStyle="xs" bold>
            {name}
          </Text>

          <ProductPrice typoStyle="xs" {...product} />

          {descriptionText && (
            <Text as="p" mb="xs" whiteSpace="pre-line">
              <ReactMarkdown>{descriptionText}</ReactMarkdown>
            </Text>
          )}

          {tariffs.length > 0 && (
            <Box>
              <TariffToggler
                onClick={() => {
                  setShowTariffs(!showTariffs);
                }}
                variant="link"
              >
                <Text typoStyle="xxs" as="span" mr={1}>
                  {showTariffs ? (
                    <FormattedMessage {...messages.hideTariffs} />
                  ) : (
                    <ShowTariffsMessage />
                  )}
                </Text>

                <Arrow
                  color="primary"
                  direction={showTariffs ? 'down' : 'right'}
                  ml="xs"
                />
              </TariffToggler>
            </Box>
          )}
        </Box>

        <Box>
          {!quantity && (
            <Button
              variant={active ? 'secondary' : 'primary'}
              small
              onClick={onAddClick}
              data-testid="buy-button"
              disabled={locked}
              shape="cool"
            >
              <Text>
                <I18NBuy />
              </Text>
            </Button>
          )}

          {quantity > 0 && (
            <Flex alignItems="center">
              <Box>
                <FormattedMessage
                  {...messages.quantity}
                  values={{ quantity }}
                />
              </Box>
              {!locked && (
                <Fragment>
                  <Box ml={2}>
                    <IconButton
                      data-testid="add-product"
                      onClick={onAddClick}
                      aria-label={intl.formatMessage(messages.ariaAdd, {
                        name
                      })}
                      rounded
                      variant="primary"
                      color="white"
                      Icon={PlusIcon}
                    />
                  </Box>
                  <Box ml={2}>
                    <IconButton
                      onClick={onRemoveClick}
                      aria-label={intl.formatMessage(messages.ariaSubtract, {
                        name
                      })}
                      rounded
                      variant="primary"
                      color="white"
                      Icon={MinusIcon}
                    />
                  </Box>
                </Fragment>
              )}
            </Flex>
          )}
        </Box>
      </Flex>

      {tariffs.length > 0 && showTariffs && (
        <Box mb="xxs" mt="xxs">
          <ConnectionPriceMatrixes items={tariffs} variant="simple" />
        </Box>
      )}
    </Base>
  );
};

export default Product;
